import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Link2Off, Printer, RefreshCw } from "lucide-react";
import { encounterQueryKeys, encountersApi } from "../../shared/api/encounters";
import { formatDate, formatDateTime } from "../../shared/format";
import { describeDose, doseDurationDays } from "../../shared/dose";
import { useClinicHeader } from "../../shared/hooks/useClinicHeader";
import type { EncounterPrescriptionItem } from "../../shared/types/encounter";
import { ErrorState } from "../../shared/ui/ErrorState";
import { Skeleton } from "../../shared/ui/Skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Legacy fallback only (#18839). Chỉ định CLS/dịch vụ used to be persisted as one paraclinical-note
 * line "Chỉ định: a; b; c" (#18591). They are their own records since #18805, and the printout now
 * reads those — but an encounter raised before that table has nothing else to print from, and a
 * service id we never recorded cannot be invented now.
 *
 * <p>The prefix check is what keeps this safe: a doctor's typed results do not start with it, so a
 * note holding results is correctly read as "no orders" rather than as one order named after the
 * findings.
 */
const SVC_PREFIX = "Chỉ định: ";
function parseServiceOrders(note: string | null | undefined): string[] {
  if (!note || !note.startsWith(SVC_PREFIX)) return [];
  return note
    .slice(SVC_PREFIX.length)
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Whether the sheet on screen is the one to print (#19171, L5).
 *
 * <p>"Hoàn tất khám &amp; In" navigates here straight after signing. The detail cached from the
 * workspace is the pre-sign copy — {@code completedAt = null} — and it renders immediately while
 * the fresh one is still in flight, so a dialog opened on "data exists" printed "Kết thúc: —".
 * Ready means: neither query is fetching, and a signed encounter actually carries its end time.
 * It also still waits for the ORDERS (#18839) so the chỉ định cannot be missing from the sheet.
 */
export function isReadyToPrint(
  detail: {
    data?: { status: string; completedAt: string | null } | undefined;
    isFetching: boolean;
  },
  orders: { isLoading: boolean; isFetching: boolean },
): boolean {
  if (!detail.data || detail.isFetching) return false;
  if (orders.isLoading || orders.isFetching) return false;
  if (detail.data.status === "SIGNED" && !detail.data.completedAt) return false;
  return true;
}

/**
 * Visit-summary print sheet — single-page A5 handout for the patient at
 * check-out. Includes clinic header, patient + visit identifiers, vitals,
 * the doctor's plan (history + assessment summary), diagnoses, prescription
 * list, and follow-up notes. Designed to be the paper the receptionist
 * staples to the prescription PDF before handing the patient out.
 *
 * <p>Print rules mirror {@code PrescriptionPrintPage}: A5 by default;
 * hide everything except {@code .vs-print} during {@code @media print};
 * preserve brand colour with {@code print-color-adjust}.
 */
export function EncounterPrintPage() {
  const { id } = useParams<{ id: string }>();
  // `?view=1` — mở từ "Xem kết quả" trên Lịch sử khám: cùng phiếu, nhưng chỉ để XEM nên không tự
  // bật hộp thoại in; nút "In" vẫn còn cho ai muốn in lại.
  const [searchParams] = useSearchParams();
  const viewOnly = searchParams.get("view") === "1";
  const navigate = useNavigate();
  const detail = useQuery({
    queryKey: encounterQueryKeys.detail(id ?? ""),
    queryFn: () => encountersApi.get(id ?? ""),
    enabled: !!id,
  });
  const orders = useQuery({
    queryKey: encounterQueryKeys.serviceOrders(id ?? ""),
    queryFn: () => encountersApi.listServiceOrders(id ?? ""),
    enabled: !!id,
  });

  // #19150 — the QR link now expires (7 days) and can be revoked. Staff renew
  // it when a patient needs the sheet again, and revoke it when a printout is
  // lost; either way the detail is refetched so the QR shown is the live one.
  const qc = useQueryClient();
  const [linkNote, setLinkNote] = useState<string | null>(null);
  // Both actions kill the link already printed or sent to the patient — ask first.
  const [confirmLink, setConfirmLink] = useState<"revoke" | "renew" | null>(
    null,
  );
  const refreshDetail = () =>
    qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(id ?? "") });
  const revokeLink = useMutation({
    mutationFn: () => encountersApi.revokeShareLink(id ?? ""),
    onSuccess: () => {
      setLinkNote("Đã thu hồi liên kết. Mã QR cũ không còn mở được phiếu.");
      return refreshDetail();
    },
    onError: () => setLinkNote("Chưa thu hồi được liên kết. Vui lòng thử lại."),
  });
  const renewLink = useMutation({
    mutationFn: () => encountersApi.renewShareLink(id ?? ""),
    onSuccess: (r) => {
      setLinkNote(
        `Đã cấp liên kết mới, dùng được đến ${formatDateTime(r.expiresAt)}. Mã QR cũ không còn mở được phiếu.`,
      );
      return refreshDetail();
    },
    onError: () => setLinkNote("Chưa cấp lại được liên kết. Vui lòng thử lại."),
  });

  // Auto-trigger the browser print dialog once the data has rendered —
  // matches PrescriptionPrintPage's UX (the operator clicks the print
  // link, the dialog opens, they select printer + paper, done).
  // Once per page visit: renewing/revoking the QR link refetches the detail
  // (#19150), and that must not pop the print dialog a second time.
  const autoPrinted = useRef(false);
  const clinic = useClinicHeader();
  // The clinic header is part of the sheet (#19168): printing before it arrives hands the patient
  // a page with no clinic on it. If it cannot be loaded at all, no automatic print — staff see why.
  const readyToPrint = isReadyToPrint(detail, orders) && clinic.header !== null;
  useEffect(() => {
    if (viewOnly) return;
    if (readyToPrint && !autoPrinted.current && typeof window !== "undefined") {
      // The short delay only lets the browser lay the sheet out; it is no longer what decides
      // whether the data is current — readyToPrint does (#19171).
      const t = window.setTimeout(() => {
        autoPrinted.current = true;
        window.print();
      }, 350);
      return () => window.clearTimeout(t);
    }
  }, [readyToPrint, viewOnly]);

  if (!id) {
    return <ErrorState error={new Error("Thiếu ID lần khám trong URL.")} />;
  }
  if (detail.isLoading || orders.isLoading) {
    return (
      <div className="p-4">
        <Skeleton height={300} />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return <ErrorState error={detail.error} />;
  }

  const e = detail.data;
  const primary = e.diagnoses.find((d) => d.primary) ?? e.diagnoses[0];
  const others = e.diagnoses.filter((d) => d !== primary);
  const v = e.vitals;
  // Structured orders are the source of truth; the note is only read for an encounter that
  // predates them (#18839). Reading the note first was how a doctor typing lab results into
  // "Kết quả XN, siêu âm bổ sung" made the orders vanish from the printed sheet.
  const svcOrders =
    (orders.data ?? []).length > 0
      ? (orders.data ?? []).map((o) => o.serviceName)
      : parseServiceOrders(e.soap.assessment.supplementaryLabUs);

  // Public, no-login view of this exam result (QR target on the printout).
  // Only available once the backend has minted a token for the encounter.
  const publicUrl = e.publicToken
    ? `${window.location.origin}/view-order/${e.publicToken}/${e.orderCode ?? ""}.html`
    : null;

  return (
    <div className="min-h-screen bg-muted p-4 print:bg-white print:p-0">
      {/* Page size + print visibility toggling can't be expressed in Tailwind
          utilities — keep this minimal style block for @page and the
          isolate-the-sheet print behaviour, plus exact colour reproduction. */}
      <style>{`
        @page { size: A5; margin: 10mm; }
        @media print {
          body * { visibility: hidden; }
          .vs-print, .vs-print * { visibility: visible; }
          .vs-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; }
        }
        .vs-print { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      `}</style>

      <div className="mb-3 flex gap-2 print:hidden">
        {viewOnly ? (
          <Button type="button" variant="secondary" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft />
            Quay lại
          </Button>
        ) : (
          <Button asChild variant="secondary" size="sm">
            <Link to={`/encounters/${id}`}>
              <ArrowLeft />
              Quay lại lần khám
            </Link>
          </Button>
        )}
        <Button type="button" size="sm" onClick={() => window.print()}>
          <Printer />
          {viewOnly ? "In" : "In lại"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={renewLink.isPending}
          onClick={() =>
            publicUrl ? setConfirmLink("renew") : renewLink.mutate()
          }
        >
          <RefreshCw />
          {publicUrl ? "Cấp lại liên kết QR" : "Cấp liên kết QR"}
        </Button>
        {publicUrl && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={revokeLink.isPending}
            onClick={() => setConfirmLink("revoke")}
          >
            <Link2Off />
            Thu hồi liên kết QR
          </Button>
        )}
      </div>
      <Dialog
        open={confirmLink !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmLink(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmLink === "revoke"
                ? "Thu hồi liên kết xem phiếu?"
                : "Cấp liên kết xem phiếu mới?"}
            </DialogTitle>
            <DialogDescription>
              {confirmLink === "revoke"
                ? "Mã QR trên phiếu đã in và liên kết đã gửi cho bệnh nhân sẽ không mở được nữa."
                : "Mã QR trên phiếu đã in và liên kết đã gửi cho bệnh nhân sẽ không mở được nữa. Liên kết mới dùng được 7 ngày."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmLink(null)}
            >
              Không
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (confirmLink === "revoke") revokeLink.mutate();
                else renewLink.mutate();
                setConfirmLink(null);
              }}
            >
              {confirmLink === "revoke" ? "Thu hồi" : "Cấp liên kết mới"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {clinic.isError && (
        <p className="mb-3 text-sm text-destructive print:hidden" role="alert">
          Không tải được thông tin phòng khám (Cài đặt → Phòng khám), nên phiếu chưa tự in. Tải lại
          trang trước khi in để phiếu có tên và địa chỉ phòng khám.
        </p>
      )}
      {linkNote && (
        <p
          className="mb-3 text-sm text-muted-foreground print:hidden"
          role="status"
        >
          {linkNote}
        </p>
      )}

      <article className="vs-print mx-auto max-w-[148mm] bg-white p-[12mm] text-[12px] leading-[1.4] text-neutral-900 shadow-[0_2px_6px_rgba(0,0,0,0.08)] print:max-w-none print:p-0 print:shadow-none">
        <header className="grid grid-cols-[1fr_auto] gap-2 border-b-2 border-black pb-1.5">
          <div>
            {/* From Cài đặt → Phòng khám; an empty field prints no line (#19168). */}
            {clinic.header && (
              <>
                <h4 className="m-0 font-semibold text-primary" data-testid="clinic-name">
                  {clinic.header.name}
                </h4>
                {clinic.header.address && (
                  <div className="text-[10px] text-neutral-600">{clinic.header.address}</div>
                )}
                {clinic.header.phoneAndTax && (
                  <div className="text-[10px] text-neutral-600">{clinic.header.phoneAndTax}</div>
                )}
                {clinic.header.email && (
                  <div className="text-[10px] text-neutral-600">{clinic.header.email}</div>
                )}
              </>
            )}
          </div>
          <div className="text-right">
            {publicUrl ? (
              <div className="text-center">
                <QRCodeSVG value={publicUrl} size={96} />
                <div className="mt-0.5 text-[9px] leading-[1.2] text-neutral-600">
                  Quét mã để xem phiếu khám online
                  {e.orderCode ? (
                    <>
                      <br />
                      {e.orderCode}
                    </>
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                <div>Phiếu tóm tắt</div>
                <div className="text-[10px]">
                  {e.id.slice(0, 8).toUpperCase()}
                </div>
              </>
            )}
          </div>
        </header>

        <h2 className="mx-0 mb-1.5 mt-0 text-center text-[15px] tracking-[0.04em]">
          PHIẾU TÓM TẮT LẦN KHÁM
        </h2>

        <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1">
          <div>
            <strong>Bệnh nhân:</strong> {e.patientName}{" "}
            {e.patientCode ? `(${e.patientCode})` : ""}
          </div>
          <div>
            <strong>Tuổi / Giới:</strong> {e.patientAge ?? "—"} /{" "}
            {genderLabel(e.patientGender)}
          </div>
          <div>
            <strong>Điện thoại:</strong> {e.patientPhone ?? "—"}
          </div>
          <div>
            <strong>Bác sĩ:</strong> {e.doctorName}
          </div>
          <div>
            <strong>Bắt đầu:</strong> {formatDateTime(e.startedAt)}
          </div>
          <div>
            <strong>Kết thúc:</strong>{" "}
            {e.completedAt ? formatDateTime(e.completedAt) : "—"}
          </div>
          {e.chiefComplaint && (
            <div className="col-span-full whitespace-pre-line">
              <strong>Lý do đến khám:</strong> {e.chiefComplaint}
            </div>
          )}
        </div>

        {hasAnyVital(v) && (
          <>
            <h3 className="mx-0 mb-1 mt-3 border-b border-neutral-400 pb-0.5 text-[12px]">
              Khám lâm sàng
            </h3>
            <div className="mt-1 grid grid-cols-4 gap-x-3 gap-y-1 text-[11px]">
              {v.systolicBp != null && v.diastolicBp != null && (
                <div>
                  HA: {v.systolicBp}/{v.diastolicBp} mmHg
                </div>
              )}
              {v.heartRate != null && <div>Mạch: {v.heartRate} lần/ph</div>}
              {v.temperatureC != null && (
                <div>Nhiệt độ: {v.temperatureC}°C</div>
              )}
              {v.respiratoryRate != null && (
                <div>Nhịp thở: {v.respiratoryRate} lần/ph</div>
              )}
              {v.spO2 != null && <div>SpO₂: {v.spO2}%</div>}
              {v.weightKg != null && <div>Cân nặng: {v.weightKg} kg</div>}
              {v.heightCm != null && <div>Chiều cao: {v.heightCm} cm</div>}
            </div>
          </>
        )}

        {(e.soap.history.presentIllness ||
          e.soap.assessment.definitivePlan) && (
          <>
            <h3 className="mx-0 mb-1 mt-3 border-b border-neutral-400 pb-0.5 text-[12px]">
              Tóm tắt khám
            </h3>
            {e.soap.history.presentIllness && (
              <div className="mb-1 whitespace-pre-line">
                <strong>Bệnh sử:</strong> {e.soap.history.presentIllness}
              </div>
            )}
            {e.soap.assessment.definitivePlan && (
              // whitespace-pre-line: the quick-advice chips join sentences with "\n", which
              // HTML otherwise collapses into one run-on line (#19171, L7).
              <div className="whitespace-pre-line" data-testid="print-advice">
                <strong>Lời dặn:</strong> {e.soap.assessment.definitivePlan}
              </div>
            )}
          </>
        )}

        {e.diagnoses.length > 0 && (
          <>
            <h3 className="mx-0 mb-1 mt-3 border-b border-neutral-400 pb-0.5 text-[12px]">
              Chẩn đoán
            </h3>
            <table className="mt-1 w-full border-collapse tabular-nums">
              <thead>
                <tr className="break-inside-avoid">
                  <th className="w-[70px] border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                    ICD-10
                  </th>
                  <th className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                    Chẩn đoán
                  </th>
                  <th className="w-[60px] border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                    Loại
                  </th>
                </tr>
              </thead>
              <tbody>
                {primary && (
                  <tr className="break-inside-avoid">
                    <td className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                      {primary.icd10Code ?? "—"}
                    </td>
                    <td className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                      {primary.label}
                    </td>
                    <td className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                      Chính
                    </td>
                  </tr>
                )}
                {others.map((d) => (
                  <tr key={d.id} className="break-inside-avoid">
                    <td className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                      {d.icd10Code ?? "—"}
                    </td>
                    <td className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                      {d.label}
                    </td>
                    <td className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                      Kèm
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {svcOrders.length > 0 && (
          <>
            <h3 className="mx-0 mb-1 mt-3 border-b border-neutral-400 pb-0.5 text-[12px]">
              Chỉ định CLS / Dịch vụ
            </h3>
            <ol className="mb-0 ml-0 mt-1 list-decimal pl-5 text-[11px]">
              {svcOrders.map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ol>
          </>
        )}

        {e.prescription.items.length > 0 && (
          <>
            <h3 className="mx-0 mb-1 mt-3 border-b border-neutral-400 pb-0.5 text-[12px]">
              Đơn thuốc
            </h3>
            <table className="mt-1 w-full border-collapse tabular-nums">
              <thead>
                <tr className="break-inside-avoid">
                  {/* #19165 — "Cách dùng" nay là câu đầy đủ ("Sáng 1 – Tối 1, sau bữa ăn ·
                      7 ngày") chứ không còn là chữ viết tắt, nên nó cần chỗ rộng nhất, không
                      phải 90px. Tên thuốc được ghim lại để cột kia nhận phần còn lại. */}
                  <th className="w-[42%] border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                    Thuốc
                  </th>
                  <th className="w-[60px] border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                    SL
                  </th>
                  <th className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                    Cách dùng
                  </th>
                </tr>
              </thead>
              <tbody>
                {e.prescription.items.map((it) => (
                  <tr key={it.id} className="break-inside-avoid">
                    <td className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                      {it.medicineName}
                      {it.medicineStrength ? ` ${it.medicineStrength}` : ""}
                    </td>
                    <td className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                      {it.quantity} {it.medicineUnit}
                    </td>
                    <td className="border border-neutral-400 px-1.5 py-0.5 text-left align-top text-[11px]">
                      {doseSummary(it)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="mt-4 flex justify-between gap-6">
          <div>
            <strong>Bệnh nhân ký tên:</strong>
            <div className="mt-6">__________________________</div>
          </div>
          <div className="text-right">
            <div>{formatDate(e.completedAt ?? e.startedAt)}</div>
            <strong>Bác sĩ điều trị:</strong>
            <div className="mt-6">{e.doctorName}</div>
          </div>
        </div>
      </article>
    </div>
  );
}

function genderLabel(g: "M" | "F" | "OTHER" | "UNKNOWN" | null): string {
  switch (g) {
    case "M":
      return "Nam";
    case "F":
      return "Nữ";
    case "OTHER":
      return "Khác";
    default:
      return "—";
  }
}

function hasAnyVital(
  v: import("../../shared/types/encounter").Vitals,
): boolean {
  return (
    v.systolicBp != null ||
    v.diastolicBp != null ||
    v.heartRate != null ||
    v.temperatureC != null ||
    v.respiratoryRate != null ||
    v.spO2 != null ||
    v.weightKg != null ||
    v.heightCm != null
  );
}

/**
 * Cách dùng in trên phiếu giao cho bệnh nhân (#19165).
 *
 * <p>Trước đây hàm này tự rút gọn thành {@code "S 1 · T 1"} và **bỏ hẳn chỉ dẫn bữa ăn** cùng lời
 * dặn của bác sĩ. Phiếu này là tờ giấy bệnh nhân mang về nhà và làm theo: mất "sau ăn" là mất
 * thông tin dùng thuốc, còn "T" thì không ai biết là trưa hay tối.
 *
 * <p>Số ngày nối thêm ở đây chứ không nằm trong {@link describeDose}: nó là con số **suy ra** từ
 * số lượng, không phải thứ bác sĩ nhập và được lưu lại.
 */
function doseSummary(it: EncounterPrescriptionItem): string {
  const days = doseDurationDays(it);
  return [describeDose(it), days ? `${days} ngày` : null].filter(Boolean).join(" · ");
}
